/**
 *
 * @module subscriptionClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/subscription.md
 * @see https://github.bamtech.co/services-commons/api-docs/tree/master/v3.0.0-subs-api
 * @see https://github.bamtech.co/services-commons/public-api/blob/master/swagger/services/subscription.yaml
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Exception from './../exception/entry';
import Token from './../token/entry';
import HttpClient from './../platforms/httpClient';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';
import Configuration from './../configuration/entry';
import Util from './../util/entry';

import SubscriptionClientEndpoint from './subscriptionClientEndpoint';
import SubscriptionClientConfiguration from './subscriptionClientConfiguration';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const AccessToken = Token.AccessToken;
const HttpMethod = Configuration.HttpMethod;
const SubscriptionClientDustReference = DustUrnReference.services.subscription.subscriptionClient;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class SubscriptionClient
 * @implements {ISubscriptionClient}
 * @desc Provides a data client that can be used to access subscription services.
 *
 */
export default class SubscriptionClient {

    /**
     *
     * @param {SubscriptionClientConfiguration} subscriptionClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(subscriptionClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'SubscriptionClient(subscriptionClientConfiguration, logger, httpClient)',
            get subscriptionClientConfiguration() {
                return `${this.ctor} needs SubscriptionClientConfiguration instance`;
            }
        };

        instanceStrict(
            subscriptionClientConfiguration,
            SubscriptionClientConfiguration,
            errorMessage.subscriptionClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {SubscriptionClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = subscriptionClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(SubscriptionClient.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Gets a list of subscriptions associated with the provided AccessToken.
     * @returns {Promise<Subscription[]>}
     *
     */
    getSubscriptions(accessToken) {

        const errorMessage = {
            method: 'SubscriptionClient.getSubscriptions(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            SubscriptionClientEndpoint.getSubscriptions
        );

        logger.info(SubscriptionClient.name, 'Attempting to get subscriptions.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: SubscriptionClient.name,
            urn: SubscriptionClientDustReference.getSubscriptions,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(data);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Associates the subscriptions from the device to the account, which are both defined in the AccessToken.
     * @returns {Promise<Void>}
     *
     */
    linkSubscriptionsFromDevice(accessToken) {

        const errorMessage = {
            method: 'SubscriptionClient.linkSubscriptionsFromDevice(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            SubscriptionClientEndpoint.linkSubscriptionsFromDevice
        );

        logger.info(SubscriptionClient.name, 'Attempting to link subscriptions from device.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: SubscriptionClient.name,
            urn: SubscriptionClientDustReference.linkSubscriptionsFromDevice,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {String} rel
     * @param {Object} body
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(accessToken, rel, body) {

        const { endpoints } = this.config;
        const endpoint = endpoints[rel];
        const { href, headers } = endpoint;
        const requestBody = body ? JSON.stringify(body) : '';
        const requestHeaders = Object.assign({}, headers);

        requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi, accessToken.token);

        return {
            url: href,
            body: requestBody,
            headers: requestHeaders
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/subscription/subscriptionClient.js